import { ServicePageTemplate, CapabilitiesGrid, BusinessImpactSection, WhyChooseUsSection, FAQSection } from "@/components/templates";
import { createServiceSchema, createFAQSchema } from "@/utils/structuredData";
import {
  Cloud,
  Server,
  Shield,
  Database,
  Cpu,
  Network,
  Lock,
  Activity,
  Coins,
  Clock,
  CloudCog,
  Code2,
  ShieldCheck,
  ServerCog,
  CheckCircle2,
  Users,
  Award,
  TrendingUp,
  Settings,
  Globe,
  Layers,
  Zap,
  RefreshCw,
} from "lucide-react";
import { DoubleDiamondProcess } from "@/components/DoubleDiamondProcess";
import { HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

// FAQ items for Cloud Solutions
const cloudFaqItems = [
  {
    question: "Which cloud platforms do you work with?",
    answer: "We're certified partners across all major cloud platforms including Amazon AWS, Microsoft Azure, and Google Cloud Platform. We help you choose the right platform based on your specific requirements, existing technology stack, and cost considerations.",
  },
  {
    question: "How do you handle cloud migration with minimal downtime?",
    answer: "We use proven migration strategies including blue-green deployments, canary releases, and parallel running systems. Our approach ensures business continuity with typically less than 1 hour of downtime for most migrations. We also provide rollback plans for complete peace of mind.",
  },
  {
    question: "What kind of cost savings can we expect from cloud migration?",
    answer: "Our clients typically see 30-50% reduction in infrastructure costs through right-sizing, reserved instances, spot pricing, and automated scaling. We also help eliminate over-provisioning and optimize resource utilization continuously.",
  },
  {
    question: "Do you provide 24/7 cloud monitoring and support?",
    answer: "Yes! We offer round-the-clock monitoring, incident response, and proactive optimization of your cloud environment. Our team uses advanced observability tools like Datadog, Prometheus, and Grafana to ensure maximum uptime and performance.",
  },
  {
    question: "How do you ensure cloud security and compliance?",
    answer: "Security is built into every layer. We implement zero-trust architecture, IAM policies, encryption at rest and in transit, and continuous security monitoring. We also help with compliance requirements including HIPAA, SOC 2, PCI-DSS, and GDPR.",
  },
  {
    question: "Can you help with Kubernetes and container orchestration?",
    answer: "Absolutely! We're experts in container orchestration using Kubernetes on EKS, AKS, and GKE. We design microservices architectures that scale automatically with demand and can be deployed independently.",
  },
];

// Cloud capabilities data
const cloudCapabilities = [
  {
    title: "Cloud Migration",
    description: "Seamless lift-and-shift or re-architecture migrations with zero downtime. We handle the complexity so you can focus on your business.",
    icon: CloudCog,
    examples: ["Lift & Shift", "Re-platforming", "Re-architecting", "Hybrid Cloud", "Multi-Cloud"],
    gradient: "from-sky-500 to-blue-500",
  },
  {
    title: "Infrastructure as Code",
    description: "Version-controlled, reproducible infrastructure using Terraform, Pulumi, and CloudFormation. Eliminate manual configuration and human error.",
    icon: Code2,
    examples: ["Terraform", "Pulumi", "CloudFormation", "Ansible", "GitOps"],
    gradient: "from-violet-500 to-purple-500",
  },
  {
    title: "Kubernetes & Containers",
    description: "Container orchestration with Kubernetes on EKS, AKS, or GKE. Microservices architecture that scales automatically with demand.",
    icon: Layers,
    examples: ["Docker", "Kubernetes", "Helm Charts", "Service Mesh", "Auto-scaling"],
    gradient: "from-orange-500 to-red-500",
  },
  {
    title: "Cloud Security",
    description: "Zero-trust architecture, compliance automation, and continuous security monitoring. HIPAA, SOC 2, PCI-DSS, and GDPR ready.",
    icon: ShieldCheck,
    examples: ["Zero Trust", "IAM Policies", "Encryption", "Compliance", "Audit Logging"],
    gradient: "from-emerald-500 to-teal-500",
  },
  {
    title: "DevOps & CI/CD",
    description: "Automated pipelines for continuous integration and deployment. Ship faster with confidence using GitHub Actions, GitLab CI, and Jenkins.",
    icon: RefreshCw,
    examples: ["GitHub Actions", "GitLab CI", "Jenkins", "ArgoCD", "Automated Testing"],
    gradient: "from-pink-500 to-rose-500",
  },
  {
    title: "Managed Cloud Services",
    description: "24/7 monitoring, incident response, and proactive optimization. We manage your cloud so you can focus on building products.",
    icon: ServerCog,
    examples: ["24/7 Monitoring", "Incident Response", "Cost Optimization", "Performance Tuning", "Backup & DR"],
    gradient: "from-indigo-500 to-blue-500",
  },
];

// Cloud benefits data
const cloudBenefits = [
  {
    title: "Reduce Infrastructure Costs",
    description: "Optimize cloud spending with right-sizing, reserved instances, and spot pricing. Our clients save 30-50% on average.",
    icon: Coins,
    stat: "40%",
    statLabel: "Avg. Cost Reduction",
  },
  {
    title: "Scale Instantly",
    description: "Handle traffic spikes without breaking a sweat. Auto-scaling infrastructure that grows with your business demands.",
    icon: TrendingUp,
    stat: "10x",
    statLabel: "Scaling Capacity",
  },
  {
    title: "Maximize Uptime",
    description: "Multi-region deployments, automated failover, and disaster recovery ensure your applications are always available.",
    icon: Activity,
    stat: "99.9%",
    statLabel: "Uptime SLA",
  },
];

const CloudSolutions = () => {
  const navigate = useNavigate();
  const serviceSchema = createServiceSchema({
    name: "Cloud Solutions",
    description: "Enterprise cloud solutions: AWS, Azure, Google Cloud migration, Kubernetes orchestration, infrastructure as code, DevOps automation, cloud security, and 24/7 managed services. Scale your infrastructure with confidence.",
    url: "https://interestech.com/services/cloud-solutions",
    serviceType: "Cloud Computing Services",
    areaServed: "Worldwide"
  });

  // FAQ Schema for rich snippets
  const faqSchema = createFAQSchema(cloudFaqItems);

  return (
    <ServicePageTemplate
      seo={{
        title: "Cloud Solutions | AWS, Azure & GCP Migration & Management | Interesting Technologies",
        description: "Enterprise cloud solutions: AWS, Azure, Google Cloud migration, Kubernetes & container orchestration, infrastructure as code, DevOps CI/CD, cloud security & compliance, and 24/7 managed services. Scale with confidence.",
        keywords: "cloud solutions, AWS cloud services, Azure cloud migration, Google Cloud Platform, Kubernetes, Docker containers, infrastructure as code, Terraform, DevOps, CI/CD pipelines, cloud security, managed cloud services, cloud cost optimization, multi-cloud architecture",
        canonical: "https://interestech.com/services/cloud-solutions",
        structuredData: [serviceSchema, faqSchema],
        ogImage: "https://interestech.com/itpl-og.webp",
        ogTitle: "Cloud Solutions | AWS, Azure & GCP Experts | Interesting Technologies",
        ogDescription: "Enterprise cloud migration, Kubernetes, DevOps, and 24/7 managed services. Scale your infrastructure across AWS, Azure, and GCP with confidence.",
      }}
      hero={{
        badge: { icon: Cloud, text: "Multi-Cloud Experts" },
        title: "Scale Your Infrastructure",
        titleHighlight: "With Confidence",
        description: "Cloud migration. Kubernetes orchestration. Infrastructure as code. DevOps automation. We build, secure, and manage cloud infrastructure across AWS, Azure, and GCP — so you can focus on your product, not your servers.",
        primaryCTA: { text: "Get Cloud Assessment", onClick: () => navigate('/contact') },
        secondaryCTA: { text: "View Architecture", onClick: () => navigate('/our-work') },
        gradient: "from-slate-900 via-sky-900 to-slate-900",
        accentColor: "cyan",
        orbitIcons: [Cloud, Server, Shield, Layers, Cpu, Lock]
      }}
      stats={{
        items: [
          { label: "Cloud Deployments", value: 500, suffix: "+", icon: Cloud },
          { label: "Uptime SLA", value: 99, suffix: ".9%", icon: Activity },
          { label: "Cost Savings", value: 40, suffix: "%", icon: Coins },
          { label: "Cloud Support", value: 24, suffix: "/7", icon: Clock }
        ],
        gradient: "from-sky-500 to-blue-500"
      }}
      features={{
        title: "Complete Cloud Services",
        subtitle: "From migration to management — we handle your entire cloud journey with expertise across all major platforms.",
        badge: { icon: CloudCog, text: "Cloud Expertise", color: "sky" },
        items: [
          { 
            text: "Cloud Migration", 
            description: "Seamless lift-and-shift or re-architecture migrations with zero downtime. We handle the complexity while you focus on business.",
            icon: CloudCog, 
            gradient: "from-sky-500 to-blue-500" 
          },
          { 
            text: "Kubernetes & Containers", 
            description: "Container orchestration with EKS, AKS, or GKE. Microservices that scale automatically with demand.",
            icon: Layers, 
            gradient: "from-orange-500 to-red-500" 
          },
          { 
            text: "Infrastructure as Code", 
            description: "Terraform and Pulumi for reproducible, version-controlled infrastructure. Eliminate manual configuration errors.",
            icon: Code2, 
            gradient: "from-violet-500 to-purple-500" 
          },
          { 
            text: "Cloud Security & Compliance", 
            description: "Zero-trust architecture, HIPAA, SOC 2, PCI-DSS compliance. Security baked into every layer.",
            icon: ShieldCheck, 
            gradient: "from-emerald-500 to-teal-500" 
          },
          { 
            text: "DevOps & CI/CD", 
            description: "Automated pipelines with GitHub Actions, GitLab CI, and ArgoCD. Ship faster with confidence.",
            icon: RefreshCw, 
            gradient: "from-pink-500 to-rose-500" 
          },
          { 
            text: "24/7 Managed Services", 
            description: "Round-the-clock monitoring, incident response, and proactive optimization of your cloud environment.",
            icon: ServerCog, 
            gradient: "from-indigo-500 to-blue-500" 
          },
        ],
      }}
      technologies={{
        title: "Cloud Platforms We Master",
        subtitle: "Certified experts across all major cloud platforms and DevOps tools.",
        badge: { icon: Cloud, text: "Platforms", color: "sky" },
        items: [
          { 
            name: "AWS", 
            description: "Market-leading cloud with 200+ services",
            tools: ["EC2", "EKS", "Lambda", "RDS", "S3"] 
          },
          { 
            name: "Azure", 
            description: "Enterprise-focused Microsoft cloud",
            tools: ["AKS", "Functions", "SQL", "Blob", "AD"] 
          },
          { 
            name: "Google Cloud", 
            description: "Data and AI-optimized cloud platform",
            tools: ["GKE", "BigQuery", "Cloud Run", "Spanner"] 
          },
          { 
            name: "Containers", 
            description: "Container orchestration at scale",
            tools: ["Docker", "Kubernetes", "Helm", "Istio"] 
          },
          { 
            name: "Infrastructure", 
            description: "Infrastructure automation tools",
            tools: ["Terraform", "Pulumi", "Ansible", "Packer"] 
          },
          { 
            name: "Observability", 
            description: "Monitoring and alerting solutions",
            tools: ["Datadog", "Prometheus", "Grafana", "PagerDuty"] 
          },
        ],
        checkColor: "text-sky-500",
      }}
      industries={{
        badge: { icon: TrendingUp, text: "Industries", color: "sky" },
        title: "Cloud Impact Across Industries",
        subtitle: "Measurable results for businesses of all sizes.",
        items: [
          { name: "FinTech & Banking", icon: Coins, projects: 85, percentage: 95 },
          { name: "Healthcare", icon: Activity, projects: 62, percentage: 90 },
          { name: "E-commerce & Retail", icon: Globe, projects: 78, percentage: 92 },
          { name: "Manufacturing & IoT", icon: Settings, projects: 45, percentage: 85 }
        ],
        progressGradient: "from-sky-500 to-blue-500",
        progressIconColor: "text-sky-500",
        statBoxes: [
          { icon: CheckCircle2, value: "500+", label: "Deployments", gradient: "from-sky-500 to-blue-500", iconColor: "text-white" },
          { icon: Activity, value: "99.9%", label: "Uptime", gradient: "from-emerald-500 to-teal-500", iconColor: "text-white" },
          { icon: Coins, value: "40%", label: "Cost Savings", gradient: "from-violet-500 to-purple-500", iconColor: "text-white" },
          { icon: Clock, value: "24/7", label: "Support", gradient: "from-orange-500 to-red-500", iconColor: "text-white" }
        ]
      }}
      cta={{
        title: "Ready to Transform Your Infrastructure?",
        description: "Get a free cloud assessment and discover how we can optimize your infrastructure for scale, security, and cost efficiency.",
        buttonText: "Get Free Assessment",
        buttonOnClick: () => navigate('/contact'),
        gradient: "from-sky-600 via-blue-600 to-indigo-600",
        buttonTextColor: "sky"
      }}
    >
      {/* Double Diamond Process */}
      <DoubleDiamondProcess />

      {/* Cloud Capabilities Section */}
      <CapabilitiesGrid
        badge={{ icon: Layers, text: "Cloud Capabilities", color: "sky" }}
        title="Everything You Need to"
        titleHighlight="Scale in the Cloud"
        subtitle="From migration strategy to 24/7 managed services — we handle your entire cloud journey with expertise across AWS, Azure, and GCP."
        items={cloudCapabilities}
        hoverColor="sky"
      />

      {/* Cloud Benefits Section */}
      <BusinessImpactSection
        badge={{ icon: TrendingUp, text: "Business Impact" }}
        title="Why"
        titleHighlight="Move to the Cloud?"
        subtitle="The cloud isn't just about technology — it's about transforming how your business operates and scales."
        items={cloudBenefits}
        accentColor="emerald"
      />

      {/* Why Choose Us Section */}
      <WhyChooseUsSection
        title="Why Choose Us for Cloud?"
        subtitle="We're not just cloud consultants — we're engineers who build and manage production systems every day."
        stats={[
          { value: "500+", label: "Cloud deployments" },
          { value: "Certified", label: "AWS, Azure & GCP" },
          { value: "24/7", label: "Support & monitoring" },
          { value: "40%", label: "Avg. cost savings" },
        ]}
        accentColor="sky"
        cta={{
          text: "Need help building applications to run on the cloud?",
          link: "/services/custom-development",
          linkText: "Explore Custom Development",
        }}
      />

      {/* FAQ Section */}
      <FAQSection
        badge={{ icon: HelpCircle, text: "FAQ", color: "sky" }}
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about our cloud solutions."
        items={cloudFaqItems}
        accentColor="sky"
        contactCTAText="Still have questions? We're here to help!"
        contactCTAButtonText="Get Cloud Assessment"
        onContactClick={() => navigate('/contact')}
      />
    </ServicePageTemplate>
  );
};

export default CloudSolutions;